import React from "react";
import { ArrowRight, Bed, Bath, Wind, Waves, MessageCircle } from "lucide-react";
import useReveal from "../hooks/useReveal";
import WindPalm from "../components/WindPalm";
import AnimatedText from "../components/AnimatedText";
import SEO from "../components/SEO";
import { SITE, SUITES, SUITE_HERO, POOL_TERRACE, PALMS } from "../mock";

const iconMap = {
  Bed: Bed,
  Bathroom: Bath,
  Climate: Wind,
  Access: Waves,
};

const Rooms = () => {
  const ref = useReveal();

  return (
    <div ref={ref} className="bg-[#f5f0e6] overflow-x-clip">
      <SEO
        title="Suites — Five king rooms above the Pacific"
        description="Five king ensuite suites at Casa Elio — each opens to ocean or mountain views, finished in linen, wood and stone, with direct access to the salt-water infinity pool."
        path="/rooms"
      />
      {/* Page header */}
      <section className="relative pt-40 pb-16 md:pt-48 md:pb-24 max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="reveal text-[10px] uppercase tracking-[0.4em] text-[#3a4a40] mb-6">
          The Suites
        </div>
        <h1 className="reveal font-serif-display text-5xl md:text-7xl lg:text-[88px] leading-[1.02] text-[#1f2a24] max-w-5xl">
          <span className="block"><AnimatedText text="Five king suites," stagger={28} baseDelay={150} /></span>
          <span className="block opacity-80" style={{ fontStyle: "italic", fontWeight: 500 }}>
            <AnimatedText text="designed around the view." stagger={26} baseDelay={650} />
          </span>
        </h1>
        <p className="reveal mt-8 max-w-2xl text-[#3a4a40] text-base md:text-lg leading-relaxed">
          Each room is private, ensuite, air-conditioned, and finished in
          natural materials — wood, linen, stone — with direct access to the
          salt-water infinity pool.
        </p>
      </section>

      {/* Hero image */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="overflow-hidden">
          <img
            src={SUITE_HERO}
            alt="Suite with ocean view"
            className="reveal-img w-full h-[88svh] md:h-[80vh] object-cover"
          />
        </div>
      </section>

      {/* Suites */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 space-y-28 md:space-y-40">
        {SUITES.map((s, idx) => {
          const reverse = idx % 2 === 1;
          const details = [
            { label: "Bed", value: s.bed },
            { label: "Bathroom", value: s.bathroom },
            { label: "Climate", value: s.climate },
            { label: "Access", value: s.access },
          ];
          return (
            <div
              key={s.id}
              className={`grid md:grid-cols-12 gap-8 md:gap-14 items-center ${
                reverse ? "md:[direction:rtl]" : ""
              }`}
            >
              <div className="md:col-span-7 [direction:ltr]">
                <div className="overflow-hidden img-hover">
                  <img
                    src={s.image}
                    alt={s.name}
                    className="reveal-img w-full aspect-[4/3] object-cover"
                  />
                </div>
              </div>
              <div className="md:col-span-5 [direction:ltr]">
                <div className="reveal text-[10px] uppercase tracking-[0.4em] text-[#3a4a40] mb-4">
                  {s.number} · {s.label}
                </div>
                <h2 className="reveal font-serif-display text-4xl md:text-5xl text-[#1f2a24] leading-[1.05]">
                  {s.name}
                </h2>
                <p className="reveal mt-6 text-[#3a4a40] text-base md:text-[17px] leading-[1.85]">
                  {s.description}
                </p>
                <ul className="reveal mt-8 grid grid-cols-2 gap-y-5 gap-x-4">
                  {details.map((d) => {
                    const Icon = iconMap[d.label];
                    return (
                      <li key={d.label} className="flex items-start gap-3">
                        <Icon size={16} className="mt-1 text-[#2f4438]" />
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.3em] text-[#3a4a40]">
                            {d.label}
                          </div>
                          <div className="text-[14px] text-[#1f2a24]">{d.value}</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          );
        })}
      </section>

      {/* Availability CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <img src={POOL_TERRACE} alt="" aria-hidden="true" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1f2a24]/65" />
        </div>
        <WindPalm
          src={PALMS.pair}
          side="right"
          offsetX={-110}
          bottom={-50}
          width={460}
          opacity={0.85}
          silhouette
          parallax={0.14}
          scrollIntensity={1.5}
          swayDuration={9.0}
          swayPhase={-2}
          hideOnMobile
        />
        <div className="relative max-w-[1100px] mx-auto px-6 md:px-10 py-28 md:py-36 text-center text-[#f5f0e6] z-10">
          <div className="reveal text-[10px] uppercase tracking-[0.4em] opacity-80 mb-5">
            Availability
          </div>
          <h2 className="reveal font-serif-display text-4xl md:text-6xl leading-[1.05]">
            Reserve a suite — or the whole house.
          </h2>
          <p className="reveal mt-6 max-w-xl mx-auto opacity-90">
            Book individual rooms for couples, or all five suites together for
            larger groups.
          </p>
          <div className="reveal mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={SITE.airbnbUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] px-7 py-4 border border-[#f5f0e6]/70 hover:bg-[#f5f0e6] hover:text-[#1f2a24] transition-colors"
            >
              Airbnb
              <ArrowRight size={14} />
            </a>
            <a
              href={`https://wa.me/${SITE.whatsappRaw}?text=${encodeURIComponent(
                "Hi Casa Elio! I'd like to book a stay in San Juan del Sur."
              )}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.3em] px-7 py-4 bg-[#f5f0e6] text-[#1f2a24] hover:bg-white transition-colors"
            >
              <MessageCircle size={14} />
              Book Now
            </a>
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] px-7 py-4 border border-[#f5f0e6]/70 hover:bg-[#f5f0e6] hover:text-[#1f2a24] transition-colors"
            >
              Booking.com
              <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Rooms;
